import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { attritionCategoriesApi } from '../../attrition-categories/attritionCategoriesApi';
import type { AttritionCategoryDetail } from '../../attrition-categories/attritionCategoryTypes';
import { useStaffOptions } from '../../personnel/useStaff';
import { attritionApi } from '../attritionApi';
import type { AttritionRequestDetail } from '../attritionTypes';
import { sampleAttritionRequest } from '../utils/sampleAttritionRequest';

const ID_PREFIX = 'attrition-request';

/**
 * File an attrition request for an employee, or correct one still open.
 *
 * **HR files it FOR the employee** (product call, 29 Oct 2026), so the employee is PICKED. The
 * category decides the checklist, which is shown under the picker as it will be asked — the
 * documents and who reviews each — so the reader sees what they are committing to before the tick.
 *
 * **On an edit the employee and the category are STATED, not offered**: the checklist was asked
 * of that person under that category, and the server refuses a move. The pickers are drawn
 * disabled with the reason in their description, so the form keeps its shape.
 *
 * **A category with no checklist is not offered** — the server refuses to file under one, and a
 * picker offers only what can be chosen. The description says how many are held back and why.
 */
export function AttritionRequestModal({
  open,
  editing,
  employeeName,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: AttritionRequestDetail | null;
  /** The resolved name of the employee on an edit — stated in the disabled picker. */
  employeeName?: string | null;
  onClose: () => void;
  onSaved: (saved: AttritionRequestDetail) => void;
}) {
  const [personnelId, setPersonnelId] = useState<string | null>(editing?.personnelId ?? null);
  const [categoryId, setCategoryId] = useState<string | null>(editing?.attritionCategoryId ?? null);
  const [effectiveDate, setEffectiveDate] = useState(editing?.effectiveDate ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const [categories, setCategories] = useState<AttritionCategoryDetail[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(editing === null);
  const [categoriesFailed, setCategoriesFailed] = useState(false);

  // Only a CREATE picks an employee; an edit states the one already filed.
  const { options: staff, loading: loadingStaff } = useStaffOptions(open && editing === null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || editing !== null) return;

    const controller = new AbortController();

    attritionCategoriesApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCategories(rows);
      })
      // A failed feed names no field, so it stays out of the save path — but it is SAID.
      .catch(() => {
        if (!controller.signal.aborted) setCategoriesFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCategories(false);
      });

    return () => controller.abort();
  }, [open, editing]);

  const fileable = useMemo(() => categories.filter((c) => c.documents.length > 0), [categories]);
  const heldBack = categories.length - fileable.length;

  const categoryOptions = useMemo(
    () =>
      editing !== null
        ? [{ value: editing.attritionCategoryId, label: editing.categoryName }]
        : fileable.map((c) => ({
            value: c.id,
            label: c.name,
            sublabel: `${c.documents.length} ${c.documents.length === 1 ? 'document' : 'documents'}`,
          })),
    [editing, fileable],
  );

  const staffOptions = useMemo(
    () =>
      editing !== null
        ? [{ value: editing.personnelId, label: employeeName ?? editing.employeeNumber ?? 'The employee on this request' }]
        : staff,
    [editing, employeeName, staff],
  );

  const chosen = editing === null ? fileable.find((c) => c.id === categoryId) ?? null : null;

  const valid = personnelId != null && categoryId != null;
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : personnelId == null
      ? 'Choose the employee.'
      : categoryId == null
        ? 'Choose the category.'
        : !confirmed
          ? 'Tick the confirm box to submit.'
          : null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      personnelId,
      attritionCategoryId: categoryId,
      effectiveDate: effectiveDate || null,
      remarks: remarks.trim() || null,
    };

    try {
      const saved = editing ? await attritionApi.update(editing.id, request) : await attritionApi.create(request);

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this request.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleAttritionRequest({ staff, categories: categoryOptions });

    // An edit's employee and category are already decided — the generator leaves them put.
    if (editing === null) {
      if (sample.personnelId) setPersonnelId(sample.personnelId);
      if (sample.attritionCategoryId) setCategoryId(sample.attritionCategoryId);
    }
    setEffectiveDate(sample.effectiveDate);
    setRemarks(sample.remarks);
  }

  const lockedReason = 'Fixed once filed — the checklist was asked of this employee under this category. Delete the request and file a new one to change it.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.requestNumber}` : 'New attrition request'}
      size="xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loadingStaff || loadingCategories }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {categoriesFailed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">The categories could not be loaded. Close this form and open it again.</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-personnel-id`}
            label="Employee"
            required
            description={
              editing ? lockedReason : 'The employee leaving the service. Only active employees are offered.'
            }
            value={personnelId}
            onChange={(next) => setPersonnelId(next || null)}
            options={staffOptions}
            loading={loadingStaff}
            placeholder="Choose an employee"
            remote
            error={personnelId != null ? error?.fieldError('personnelId') : 'Choose the employee.'}
            disabled={saving || editing !== null}
          />

          <Select
            id={`${ID_PREFIX}-attrition-category-id`}
            label="Category"
            required
            description={
              editing
                ? lockedReason
                : heldBack > 0
                  ? `The kind of separation. ${heldBack} ${heldBack === 1 ? 'category has' : 'categories have'} no documents on ${heldBack === 1 ? 'its' : 'their'} checklist yet and ${heldBack === 1 ? 'is' : 'are'} not offered — see Attrition → Settings.`
                  : 'The kind of separation. It decides which documents the request asks for.'
            }
            value={categoryId}
            onChange={(next) => setCategoryId(next || null)}
            options={categoryOptions}
            loading={loadingCategories}
            placeholder="Choose a category"
            remote
            sort="none"
            error={categoryId != null ? error?.fieldError('attritionCategoryId') : 'Choose the category.'}
            disabled={saving || editing !== null}
          />

          <DateField
            id={`${ID_PREFIX}-effective-date`}
            label="Effective date"
            description="The date the separation takes effect — the retirement date, or the last day after a resignation. Leave it empty if it is not settled yet."
            value={effectiveDate}
            onChange={setEffectiveDate}
            error={error?.fieldError('effectiveDate')}
            disabled={saving}
          />
        </div>

        {/*
          THE CHECKLIST THIS REQUEST WILL ASK FOR — read-only facts, so the field table. Drawn only
          once a category is chosen on a create; an edit's checklist is on the request's own page.
        */}
        {chosen ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-slate-500">
              Filing this asks for {chosen.documents.length}{' '}
              {chosen.documents.length === 1 ? 'document' : 'documents'}, each reviewed by the account named.
            </p>
            <PreviewTable
              rows={chosen.documents.map((d) => ({
                label: d.name,
                value: `Reviewed by ${d.reviewerName ?? 'an account no longer listed'}`,
              }))}
            />
          </div>
        ) : null}

        <Textarea
          id={`${ID_PREFIX}-remarks`}
          label="Remarks"
          description="Anything the reviewers should know about this separation. It stays on the request."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
